/**
 * AlertDialog — non-dismissible confirmation overlay (ADR-114).
 *
 * Always centered (even on mobile), never closes on backdrop click / drag /
 * ESC unless the consumer explicitly opts in. Use for destructive
 * confirmations. Parts mirror the shadcn/Radix contract.
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ButtonHTMLAttributes,
  type ReactNode,
} from 'react';
import { cn } from '../../../lib/utils';
import type { OverlaySize } from './constants';
import { OverlayShell } from './OverlayShell';
import { DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './parts';

interface AlertDialogContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
}

const AlertDialogContext = createContext<AlertDialogContextValue | null>(null);

function useAlertDialogContext(): AlertDialogContextValue {
  const context = useContext(AlertDialogContext);
  if (!context) {
    throw new Error('AlertDialog parts must be used inside <AlertDialog>.');
  }
  return context;
}

export interface AlertDialogProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  defaultOpen?: boolean;
  children: ReactNode;
}

export function AlertDialog({
  open,
  onOpenChange,
  defaultOpen = false,
  children,
}: AlertDialogProps) {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const isControlled = open !== undefined;

  const setOpen = useCallback(
    (next: boolean) => {
      if (!isControlled) setInternalOpen(next);
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange],
  );

  const value = useMemo<AlertDialogContextValue>(
    () => ({ open: isControlled ? Boolean(open) : internalOpen, setOpen }),
    [isControlled, open, internalOpen, setOpen],
  );

  return (
    <AlertDialogContext.Provider value={value}>{children}</AlertDialogContext.Provider>
  );
}

export interface AlertDialogContentProps {
  children: ReactNode;
  className?: string;
  size?: OverlaySize;
  /** Destructive confirmations must keep this false (default). */
  dismissible?: boolean;
  ariaLabel?: string;
  ariaLabelledBy?: string;
  'data-testid'?: string;
}

export function AlertDialogContent({
  children,
  className,
  size = 'md',
  dismissible = false,
  ariaLabel,
  ariaLabelledBy,
  'data-testid': testId,
}: AlertDialogContentProps) {
  const { open, setOpen } = useAlertDialogContext();

  return (
    <OverlayShell
      open={open}
      onClose={() => setOpen(false)}
      dismissible={dismissible}
      mode="dialog"
      role="alertdialog"
      size={size}
      className={cn('sm:rounded-lg', className)}
      ariaLabel={ariaLabel}
      ariaLabelledBy={ariaLabelledBy}
      showClose={dismissible}
      testId={testId ?? 'alert-dialog-content'}
    >
      {children}
    </OverlayShell>
  );
}

export interface AlertDialogActionProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode;
}

export function AlertDialogAction({ className, ...props }: AlertDialogActionProps) {
  const { setOpen } = useAlertDialogContext();
  return (
    <button
      type="button"
      className={cn(
        'inline-flex h-9 items-center justify-center rounded-md bg-red-600 px-4 text-sm font-medium text-white transition-colors hover:bg-red-700 disabled:opacity-50',
        className,
      )}
      onClick={(event) => {
        props.onClick?.(event);
        setOpen(false);
      }}
      {...props}
    />
  );
}

export interface AlertDialogCancelProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode;
}

export function AlertDialogCancel({ className, ...props }: AlertDialogCancelProps) {
  const { setOpen } = useAlertDialogContext();
  return (
    <button
      type="button"
      className={cn(
        'mt-2 inline-flex h-9 items-center justify-center rounded-md border border-gray-300 bg-white px-4 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 sm:mt-0 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200',
        className,
      )}
      onClick={(event) => {
        props.onClick?.(event);
        setOpen(false);
      }}
      {...props}
    />
  );
}

export {
  DialogDescription as AlertDialogDescription,
  DialogFooter as AlertDialogFooter,
  DialogHeader as AlertDialogHeader,
  DialogTitle as AlertDialogTitle,
};
